import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, UsersThree, Check, XCircle } from "@phosphor-icons/react";
import { friendsApi } from "@/lib/apiClient";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { dispatchFriendStatusChange } from "@/components/FriendButton";

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
}

export default function FriendRequestsModal({ open, onClose, onFriendAccepted }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyHandle, setBusyHandle] = useState(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        friendsApi.myRequests()
            .then((data) => setRequests(Array.isArray(data) ? data : []))
            .catch(() => setRequests([]))
            .finally(() => setLoading(false));
    }, [open]);

    // Close on Escape key
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    const removeRequest = (handle) => {
        setRequests((prev) => prev.filter((r) => r.sender_handle !== handle));
        dispatchFriendStatusChange(handle);
    };

    const accept = async (handle) => {
        if (busyHandle) return;
        setBusyHandle(handle);
        try {
            await friendsApi.accept(handle);
            toast.success(`You are now friends with @${handle}.`);
            removeRequest(handle);
            onFriendAccepted?.();
        } catch {
            toast.error("An error occurred.");
        } finally {
            setBusyHandle(null);
        }
    };

    const decline = async (handle) => {
        if (busyHandle) return;
        setBusyHandle(handle);
        try {
            await friendsApi.reject(handle);
            toast.success("Friend request declined.");
            removeRequest(handle);
        } catch {
            toast.error("An error occurred.");
        } finally {
            setBusyHandle(null);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50"
                        style={{ background: "rgba(5,10,17,0.75)", backdropFilter: "blur(4px)" }}
                        onClick={onClose}
                    />
                    {/* Panel */}
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.97 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none px-4"
                    >
                        <div
                            className="pointer-events-auto w-full max-w-md rounded-3xl overflow-hidden"
                            style={{
                                background: "var(--navy)",
                                border: "1px solid rgba(212,175,55,0.25)",
                                boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
                            }}
                            data-testid="friend-requests-modal"
                        >
                            {/* Header */}
                            <div
                                className="px-6 py-5 flex items-center justify-between border-b"
                                style={{ borderColor: "rgba(255,255,255,0.06)" }}
                            >
                                <div className="flex items-center gap-2">
                                    <UsersThree size={18} color="#D4AF37" weight="fill" />
                                    <div className="font-serif text-xl text-white">Friend Requests</div>
                                    {requests.length > 0 && (
                                        <span
                                            className="text-[11px] font-bold min-w-[22px] h-[22px] rounded-full grid place-items-center px-1"
                                            style={{ background: "var(--gold)", color: "var(--navy)" }}
                                        >
                                            {requests.length}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/10 transition-colors"
                                    data-testid="friend-requests-close"
                                >
                                    <X size={14} color="rgba(255,255,255,0.6)" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="max-h-[420px] overflow-y-auto divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                                {loading ? (
                                    <div className="p-10 text-center text-white/50 text-sm">Loading requests…</div>
                                ) : requests.length === 0 ? (
                                    <div className="p-10 text-center" data-testid="friend-requests-empty">
                                        <UsersThree size={32} color="rgba(255,255,255,0.2)" className="mx-auto mb-3" />
                                        <div className="text-white/50 text-sm">No pending friend requests.</div>
                                    </div>
                                ) : (
                                    requests.map((req) => (
                                        <div
                                            key={req.id}
                                            className="flex items-center gap-3 px-5 py-4 hover:bg-white/[0.025] transition-colors"
                                            data-testid={`friend-req-row-${req.sender_handle}`}
                                        >
                                            {/* Avatar — links to their profile and closes modal */}
                                            <Link to={`/profile/${req.sender_handle}`} onClick={onClose}>
                                                <img
                                                    src={req.sender_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${req.sender_handle}`}
                                                    alt=""
                                                    className="w-11 h-11 rounded-full object-cover shrink-0"
                                                    style={{ border: "1.5px solid rgba(212,175,55,0.3)" }}
                                                />
                                            </Link>

                                            {/* Name + handle + time */}
                                            <div className="flex-1 min-w-0">
                                                <Link
                                                    to={`/profile/${req.sender_handle}`}
                                                    onClick={onClose}
                                                    className="text-sm text-white font-medium hover:text-[#D4AF37] transition-colors line-clamp-1"
                                                >
                                                    {req.sender_name || `@${req.sender_handle}`}
                                                </Link>
                                                <div className="text-[11px] text-white/45">
                                                    @{req.sender_handle} · {timeAgo(req.created_at)}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex gap-2 shrink-0">
                                                <button
                                                    onClick={() => accept(req.sender_handle)}
                                                    disabled={!!busyHandle}
                                                    className="inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors disabled:opacity-50"
                                                    style={{ background: "#10b981", color: "white" }}
                                                    data-testid={`accept-req-${req.sender_handle}`}
                                                >
                                                    <Check size={11} weight="bold" /> Accept
                                                </button>
                                                <button
                                                    onClick={() => decline(req.sender_handle)}
                                                    disabled={!!busyHandle}
                                                    className="inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors disabled:opacity-50"
                                                    style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}
                                                    data-testid={`decline-req-${req.sender_handle}`}
                                                >
                                                    <XCircle size={11} weight="bold" /> Decline
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
